/**
 * RichTextBodyField — `RichHtmlField` wrapped with a minimal formatting
 * toolbar (bold/italic/underline/link/bulleted-list/numbered-list).
 *
 * U9 (F2c, Gong Flow follow-up bundle). Key Decision 2: a boolean toolbar
 * prop on the shared `RichHtmlField` was REJECTED — that component stays
 * untouched (its DOM-island/seed-key/paste-sanitize contract is load-bearing
 * for every other consumer). This is a NEW wrapper component instead. Props
 * are exactly `RichHtmlFieldProps` (`value`/`seedKey`/`onChange`/`className`/
 * `disabled`) so it is a drop-in swap wherever a `bodyFormat === 'rich'` play
 * type needs the toolbar (currently: `StepOverrideEditor`'s Gong step body).
 *
 * Toolbar scope (Key Decision 4): EXACTLY six buttons, 1:1 with the server
 * allowlist `sanitizeStepDraftHtml` enforces on write
 * (`src/services/flow/step-drafts.ts`: `p, br, b, i, u, strong, em, a, ul,
 * ol, li, div`). `p`/`br`/`div` are structural output the browser produces
 * on its own, not toolbar-driven, so they need no button. `strong`/`em`
 * (U5, bug 4) are allowlisted so Gong's own natively-authored formatting
 * survives sanitization — the toolbar deliberately keeps emitting `b`/`i`,
 * not `strong`/`em`; this is an allowlist widening, not a toolbar change.
 * No font/size/color/align/blockquote controls — those would be silently
 * stripped by the sanitizer today (see
 * `explorations/rich-text-body-editor/notes.md` §1).
 *
 * Command layer (Key Decision 3): hand-rolled Selection/Range API calls, NOT
 * `document.execCommand` (unavailable/inert in this repo's jsdom test
 * environment, and its markup output is notoriously browser-inconsistent —
 * see the exploration notes' Option A gotchas) and NOT a rich-text library
 * (TipTap/Lexical/etc — no new dependency for a 2-field surface). Each
 * command wraps the live DOM selection's contents in the target element
 * directly, which is "basic Selection-API/execCommand-style" in intent
 * without either of execCommand's downsides.
 *
 * DOM-island discipline (unchanged from `RichHtmlField`): this component
 * NEVER renders its own `contentEditable` element and never touches
 * `dangerouslySetInnerHTML` — `terra/no-contenteditable-with-inner-html`
 * would never even see a violating pattern here, since the only
 * `contentEditable` node is the one `RichHtmlField` itself owns. Toolbar
 * buttons reach into that DOM node imperatively (via a container `ref` +
 * `querySelector`), exactly the way `RichHtmlField`'s own paste handler
 * reaches into its div — React never owns the editable region's children.
 *
 * Selection preservation: every button uses `onMouseDown` (not `onClick`) to
 * call `preventDefault()`, which stops the browser's default "move focus to
 * the button" behavior. Without this, mousedown would blur the contentEditable
 * and collapse/clear `window.getSelection()` before the click handler ever
 * runs, so every command would see no selection at all.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TextB,
  TextItalic,
  TextUnderline,
  LinkSimple,
  ListBullets,
  ListNumbers,
} from '@phosphor-icons/react';
import { IconButton } from '@clay-run/glaze/iconbutton';
import type { IconType } from '@clay-run/glaze/icon';
import { RichHtmlField, type RichHtmlFieldProps } from './RichHtmlField';
import { cn } from '../../lib/cn';

/** Drop-in-compatible props — identical shape to `RichHtmlFieldProps`. */
export type RichTextBodyFieldProps = RichHtmlFieldProps;

/** The one `[contenteditable]` node `RichHtmlField` renders inside `container`, or `null` before mount. */
function getEditableRoot(container: HTMLElement | null): HTMLElement | null {
  return container?.querySelector<HTMLElement>('[contenteditable]') ?? null;
}

/**
 * The live selection's range, but only if it actually lives inside `root` —
 * a stale selection left over from clicking elsewhere on the page (or no
 * selection at all) must never be operated on.
 */
function getOwnRange(root: HTMLElement): Range | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;
  return range;
}

/** Move the collapsed caret to just after `node`, so typing continues past the just-inserted markup. */
function collapseCaretAfter(node: Node) {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

/** Extract the range's contents into `tagName`, wrapping in place, with optional attributes (e.g. `href`). */
function wrapRangeIn(range: Range, tagName: string, attrs?: Record<string, string>): HTMLElement {
  const wrapper = document.createElement(tagName);
  if (attrs) {
    for (const [name, value] of Object.entries(attrs)) wrapper.setAttribute(name, value);
  }
  wrapper.appendChild(range.extractContents());
  range.insertNode(wrapper);
  return wrapper;
}

/**
 * Line-wrapper tags a multi-line selection can be made of: Gong bodies use
 * one `<div data-default-styles>` per line; a plain `<p>` is the other
 * common shape (e.g. the toolbar's own output, or pasted content).
 */
const LINE_BLOCK_TAGS = new Set(['DIV', 'P']);

function isLineBlockElement(node: Node): node is HTMLElement {
  return node.nodeType === Node.ELEMENT_NODE && LINE_BLOCK_TAGS.has((node as Element).tagName);
}

/** Whitespace-only (or empty) text content — used to skip blank lines rather than emit an empty `<li>`. */
function isBlank(node: Node): boolean {
  return (node.textContent ?? '').trim() === '';
}

/**
 * Split a multi-line fragment (as extracted by `range.extractContents()` for
 * a `wrapRangeAsList` selection) into one `<li>` per line and append them,
 * in order, to `list` (bug 3b-i). A block-level top-level child (`<div>`/`<p>`
 * — one per authored line) becomes its own `<li>` built from that block's
 * children; the line-wrapper element itself is dropped, so the sanitizer
 * never sees `<li><div>`. Consecutive non-block top-level nodes (plain text /
 * inline marks — the single-line case existing tests pin) accumulate into
 * one shared `<li>` until the next block boundary, so a single-line
 * selection still yields exactly one `<li>`. A blank/whitespace-only line
 * produces no `<li>` at all.
 */
function appendLineItems(list: HTMLElement, fragment: DocumentFragment): void {
  let pendingInlineItem: HTMLElement | null = null;

  const flushPendingInlineItem = () => {
    if (pendingInlineItem && !isBlank(pendingInlineItem)) list.appendChild(pendingInlineItem);
    pendingInlineItem = null;
  };

  for (const node of Array.from(fragment.childNodes)) {
    if (isLineBlockElement(node)) {
      flushPendingInlineItem();
      const item = document.createElement('li');
      while (node.firstChild) item.appendChild(node.firstChild);
      if (!isBlank(item)) list.appendChild(item);
    } else {
      pendingInlineItem ??= document.createElement('li');
      pendingInlineItem.appendChild(node);
    }
  }
  flushPendingInlineItem();
}

/** Nearest ancestor (or self) carrying the `contenteditable` attribute — the editable root `RichHtmlField` owns, found without needing it threaded through as a parameter (keeps `wrapRangeAsList`'s signature/callers unchanged). */
function closestEditableRoot(node: Node): HTMLElement | null {
  const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  return element?.closest<HTMLElement>('[contenteditable]') ?? null;
}

/** Nearest `<p>` ancestor of `node`, stopping at (and not past) `root`. */
function findParagraphAncestor(node: Node, root: HTMLElement | null): HTMLElement | null {
  let el: Node | null = node.nodeType === Node.ELEMENT_NODE ? node : node.parentNode;
  while (el instanceof HTMLElement) {
    if (el.tagName === 'P') return el;
    if (el === root) return null;
    el = el.parentNode;
  }
  return null;
}

/**
 * A `<ul>`/`<ol>` can never be inserted inside a `<p>` — the browser's HTML
 * parser auto-closes `<p>` on the next reseed innerHTML round-trip (block
 * content isn't legal inside `<p>`), silently restructuring content (bug
 * 3b-ii). If `paragraph` is an ancestor of the (now-collapsed) `range`,
 * split it: everything at/after the collapse point moves into a new
 * trailing `<p>` inserted after the original, and `list` is inserted
 * between the two halves. Either half is dropped if left empty.
 */
function insertListExitingParagraph(paragraph: HTMLElement, range: Range, list: HTMLElement): void {
  const parent = paragraph.parentNode;
  if (!parent) {
    range.insertNode(list);
    return;
  }

  const trailingRange = document.createRange();
  trailingRange.setStart(range.startContainer, range.startOffset);
  trailingRange.setEnd(paragraph, paragraph.childNodes.length);
  const trailingContent = trailingRange.extractContents();

  const trailingParagraph = document.createElement('p');
  trailingParagraph.appendChild(trailingContent);

  parent.insertBefore(trailingParagraph, paragraph.nextSibling);
  parent.insertBefore(list, trailingParagraph);

  if (isBlank(paragraph)) paragraph.remove();
  if (isBlank(trailingParagraph)) trailingParagraph.remove();
}

/**
 * Inline formatting/link ancestor tags whose loss during listify would
 * silently drop marks (bug 4a). `STRONG`/`EM` added (Codex adversarial
 * review follow-up, gong-flow-followup-bundle bugfixes): U5 widened the
 * server sanitizer to preserve Gong's native `<strong>`/`<em>`, so a real
 * Gong-authored selection can now be listified — these two tags need the
 * same loss-prevention as `B`/`I`/`U`/`A`.
 */
const FORMAT_ANCESTOR_TAGS = new Set(['B', 'I', 'U', 'A', 'STRONG', 'EM']);

/**
 * The full chain of inline formatting/link ancestors (`<b>`/`<i>`/`<u>`/`<a>`/
 * `<strong>`/`<em>`) that `range.extractContents()` would silently drop, in
 * innermost-to-outermost order (nearest the Text node first) — bug 4a, and
 * (Codex review follow-up) the multi-ancestor generalization of it.
 *
 * `Range.extractContents()` has documented DOM behavior: when the range's
 * start and end containers are the SAME node (a selection fully inside one
 * Text node — e.g. a partial or full selection of a single inline-formatted
 * run), the spec's extraction algorithm clones just that Text node's
 * substring directly into the fragment; it does NOT clone any of the
 * enclosing `<b>`/`<i>`/`<u>`/`<a>`/`<strong>`/`<em>` ancestors. Every other
 * selection shape (spanning multiple nodes) goes through the general
 * clone-ancestors-on-both-sides path, which does NOT lose the ancestors —
 * so this check is deliberately narrow: only the same-Text-node case is
 * affected. Real Gong content nests up to FOUR of these
 * (`<a><u><em><strong>`) — returning only the nearest match (the original
 * single-ancestor version of this function) silently dropped the rest, so
 * this collects every matching ancestor found while walking up, continuing
 * past any non-matching, non-line-block element transparently (same as
 * before), and stops at the first line-block ancestor (`<div>`/`<p>`) or
 * `root` (same as before) — a formatting ancestor beyond the line being
 * split is not "lost" by this operation.
 */
function findFormatAncestorChainForRange(range: Range, root: HTMLElement | null): HTMLElement[] {
  if (range.collapsed) return [];
  if (range.startContainer !== range.endContainer) return [];
  if (range.startContainer.nodeType !== Node.TEXT_NODE) return [];

  const chain: HTMLElement[] = [];
  let el: Node | null = range.startContainer.parentNode;
  while (el && el !== root) {
    if (el.nodeType === Node.ELEMENT_NODE) {
      const tag = (el as Element).tagName;
      if (FORMAT_ANCESTOR_TAGS.has(tag)) chain.push(el as HTMLElement);
      else if (LINE_BLOCK_TAGS.has(tag)) break;
    }
    el = el.parentNode;
  }
  return chain;
}

/**
 * Extract the range's contents, split into one `<li>` per line (per-block
 * split, bug 3b-i — see `appendLineItems`), and insert the resulting
 * `<ul>`/`<ol>` in place — exiting any enclosing `<p>` first so the list
 * never ends up nested inside one (bug 3b-ii — see
 * `insertListExitingParagraph`).
 *
 * Three follow-up fixes on top of that (Codex adversarial review):
 *  - bug 4a: if extraction would silently drop inline formatting/link
 *    ancestors (see `findFormatAncestorChainForRange`), re-wrap the
 *    extracted content in clones of that FULL chain (nesting them in the
 *    same order as the original — innermost-to-the-text wrapped first)
 *    before splitting into `<li>`s, so listifying a partial selection
 *    inside `<b>`/`<i>`/`<u>`/`<a>`/`<strong>`/`<em>` never strips any of
 *    the formatting, even when several of these nest (real Gong content
 *    nests up to four: `<a><u><em><strong>`).
 *  - bug 4c: a selection that resolves to zero non-blank lines (e.g.
 *    selecting only a blank `<div><br></div>`) is a true no-op — returns
 *    `null` instead of inserting an empty `<ul>`/`<ol>`. Callers must check
 *    for `null` (see `applyList`'s existing collapsed-selection no-op guard
 *    for the same pattern).
 */
function wrapRangeAsList(range: Range, listTag: 'ul' | 'ol'): HTMLElement | null {
  const rootBeforeExtract = closestEditableRoot(range.startContainer);
  const lostFormatChain = findFormatAncestorChainForRange(range, rootBeforeExtract);

  // Dry run against a non-destructive clone first: detect the "resolves to
  // zero lines" no-op case (bug 4c) before mutating the live DOM at all.
  const previewList = document.createElement(listTag);
  appendLineItems(previewList, range.cloneContents());
  if (previewList.children.length === 0) return null;

  const list = document.createElement(listTag);
  const fragment = range.extractContents();
  if (lostFormatChain.length > 0) {
    // Rebuild the chain around the extracted content, innermost first: the
    // first ancestor (nearest the original Text node) receives the
    // fragment's children directly, then each subsequent (outer) ancestor
    // wraps the previous clone, so the final nesting mirrors the source
    // exactly (e.g. `<a><u><em><strong>text</strong></em></u></a>`).
    let wrapped: HTMLElement | null = null;
    for (const ancestor of lostFormatChain) {
      const clone = ancestor.cloneNode(false) as HTMLElement;
      if (wrapped) {
        clone.appendChild(wrapped);
      } else {
        while (fragment.firstChild) clone.appendChild(fragment.firstChild);
      }
      wrapped = clone;
    }
    if (wrapped) fragment.appendChild(wrapped);
  }
  appendLineItems(list, fragment);

  const root = closestEditableRoot(range.startContainer);
  const paragraph = findParagraphAncestor(range.startContainer, root);
  if (paragraph) {
    insertListExitingParagraph(paragraph, range, list);
  } else {
    range.insertNode(list);
  }
  return list;
}

/**
 * The DOM-level mirror of `appendLineItems`'s per-block split: reverses
 * `list` back into one plain line element (a `<div>`, matching Gong's own
 * per-line convention) per `<li>`, in the same order, and removes `list`
 * itself. Exposed at module scope (not a local closure) because U2's list
 * toggle-off wiring calls this directly from `applyList`. Pure DOM
 * operation — does not call `onChange`; the caller commits the result.
 *
 * `caretLineIndex` (bug 4b, Codex review follow-up): which resulting line's
 * end the caret should be restored to, given as an index into `list`'s
 * original `<li>` children — NOT always the first line. Defaults to `0` so
 * direct pure-DOM callers (see the `unwrapList` tests below, which assert on
 * DOM shape only) keep the pre-fix caret behavior; `applyList` passes the
 * index of whichever `<li>` actually contained the caret/selection before
 * unwrapping.
 */
export function unwrapList(list: HTMLElement, root: HTMLElement, caretLineIndex = 0): void {
  const parent = list.parentNode;
  if (!parent) return;

  const lines: HTMLElement[] = [];
  for (const li of Array.from(list.children)) {
    if (li.tagName !== 'LI') continue;
    const line = document.createElement('div');
    while (li.firstChild) line.appendChild(li.firstChild);
    lines.push(line);
  }

  for (const line of lines) parent.insertBefore(line, list);
  parent.removeChild(list);

  const targetLine = lines[caretLineIndex] ?? lines[0];
  if (targetLine && root.contains(targetLine)) {
    collapseCaretAfter(targetLine);
  }
}

/** Tags `getActiveFormats` tracks — the toolbar's togglable formats. `a` is tracked as of L2 (link is now a toggle: active-state + unlink), routed through the same `closestAncestorTag` branch as `ul`/`ol` below. */
const FORMAT_TAGS = ['b', 'i', 'u', 'a', 'ul', 'ol'] as const;
type ActiveFormat = (typeof FORMAT_TAGS)[number];

/**
 * When a Range's `commonAncestorContainer` is an Element and the range spans
 * EXACTLY one of that element's children — the shape `range.selectNode(child)`
 * produces (`startContainer === endContainer === commonAncestorContainer` and
 * `endOffset === startOffset + 1`) — `commonAncestorContainer` sits ABOVE the
 * one selected child rather than inside or below it (Codex adversarial review
 * follow-up). An upward ancestor walk starting from `commonAncestorContainer`
 * can never see a match that lives entirely within that child: the child is a
 * sibling-position node WITHIN the range, not an ancestor OF
 * `commonAncestorContainer`. Concretely: `<a href="..."><strong>Only linked
 * text</strong></a>` as the editor's ENTIRE content, selected via
 * `range.selectNode(anchorEl)`, makes `commonAncestorContainer` the editor
 * root with `startOffset`/`endOffset` `0`/`1` — walking up from root finds
 * nothing, even though the whole selection is one linked, bolded run.
 *
 * This detects that shape and, if found, walks DOWNWARD from the one selected
 * child through single-non-blank-child chains (the mirror image of the
 * upward walk): a chain like `<a><strong>text</strong></a>` has exactly one
 * non-blank child at each level, so "the entire child is wrapped by a tag
 * matching `matchesTag`" is true whenever that tag sits anywhere on the one
 * path from the selected child down to its content. A node with more than one
 * non-blank child (or a non-Element node) breaks the chain — the assumption a
 * whole-child selection makes ("this one node accounts for the entire
 * selection") no longer proves any inner tag wraps ALL of it, so this stops
 * rather than guessing. Narrow by design (not a full range-intersection
 * engine) — it only ever resolves the single-full-child-selection shape.
 */
function findFullChildSelectionMatch(range: Range, matchesTag: (tagName: string) => boolean): HTMLElement | null {
  const container = range.commonAncestorContainer;
  if (container.nodeType !== Node.ELEMENT_NODE) return null;
  if (range.startContainer !== container || range.endContainer !== container) return null;
  if (range.endOffset !== range.startOffset + 1) return null;

  let node: Node | undefined = container.childNodes[range.startOffset];
  while (node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const element = node as Element;
    if (matchesTag(element.tagName)) return element as HTMLElement;
    const nonBlankChildren = Array.from(element.childNodes).filter(
      child => !(child.nodeType === Node.TEXT_NODE && isBlank(child)),
    );
    node = nonBlankChildren.length === 1 ? nonBlankChildren[0] : undefined;
  }
  return null;
}

/**
 * The nearest ancestor of `range`'s `commonAncestorContainer` (inclusive of
 * itself) whose tag name is `tag`, stopping at (and not past) `root`. Shared
 * by `getActiveFormats` (boolean membership) and the inline/list toggle-off
 * paths (need the actual element to unwrap), so both always agree on what
 * counts as "active" — U2's Key Technical Decision 6.
 *
 * Falls back to `findFullChildSelectionMatch` (Codex adversarial review
 * follow-up) when the upward walk finds nothing — covers the
 * `range.selectNode(...)`-shaped selection the upward walk structurally
 * cannot see (see that function's doc comment).
 */
function closestAncestorTag(range: Range, tag: string, root: HTMLElement): HTMLElement | null {
  let el: Node | null = range.commonAncestorContainer;
  while (el && el !== root) {
    if (el.nodeType === Node.ELEMENT_NODE && (el as Element).tagName.toLowerCase() === tag) {
      return el as HTMLElement;
    }
    el = el.parentNode;
  }
  return findFullChildSelectionMatch(range, tagName => tagName.toLowerCase() === tag);
}

/**
 * Canonical alias tags for the toolbar's inline formats, keyed by the tag the
 * toolbar itself emits on apply (Codex adversarial review follow-up,
 * gong-flow-followup-bundle bugfixes): Gong authors bold/italic natively as
 * `<strong>`/`<em>`, and U5 widened the server sanitizer to preserve them —
 * so a caret/selection inside Gong-authored content must read Bold/Italic as
 * active, and toggle-off must find and unwrap that same element, even though
 * the toolbar never emits `<strong>`/`<em>` itself (only ever `<b>`/`<i>`).
 * Underline has no native alias in this codebase's scope, so its list is
 * just itself.
 */
const FORMAT_TAG_ALIASES: Record<'b' | 'i' | 'u', readonly string[]> = {
  b: ['B', 'STRONG'],
  i: ['I', 'EM'],
  u: ['U'],
};

/**
 * Alias-aware sibling of `closestAncestorTag`, scoped to the toolbar's three
 * inline formats: nearest ancestor (or self) of `range`'s
 * `commonAncestorContainer` whose tag name is `tagName` OR one of its
 * canonical aliases (see `FORMAT_TAG_ALIASES`), stopping at (and not past)
 * `root`. Shared by `getActiveFormats` and `applyInline`'s toggle-off path —
 * per this file's Key Technical Decision 6, both call paths must always agree
 * on what counts as active, so the alias list lives in exactly one place and
 * both read through this one helper rather than each keeping their own copy.
 *
 * Falls back to `findFullChildSelectionMatch` (Codex adversarial review
 * follow-up) when the upward walk finds nothing, same as `closestAncestorTag`
 * — a bold/italic run reached only via a `range.selectNode(...)`-shaped
 * selection (e.g. the entire editor content is one linked `<b>` run) is
 * otherwise invisible to the upward walk. See that function's doc comment.
 */
function closestFormatAncestor(range: Range, tagName: 'b' | 'i' | 'u', root: HTMLElement): HTMLElement | null {
  const aliases = FORMAT_TAG_ALIASES[tagName];
  let el: Node | null = range.commonAncestorContainer;
  while (el && el !== root) {
    if (el.nodeType === Node.ELEMENT_NODE && aliases.includes((el as Element).tagName)) {
      return el as HTMLElement;
    }
    el = el.parentNode;
  }
  return findFullChildSelectionMatch(range, tag => aliases.includes(tag));
}

/**
 * Which formats the given range/caret is "inside", for toolbar active-state
 * highlighting (bug 2, U2). Walks ancestors from `range.commonAncestorContainer`
 * up to (not past) `root`.
 *
 * This single starting point — `commonAncestorContainer` — is what gives a
 * collapsed caret and an expanded selection the right, DIFFERENT semantics
 * for free (Key Technical Decision 6): a collapsed caret's common ancestor is
 * wherever the caret sits, so a caret inside `<b>` reads as bold-active. An
 * expanded selection's common ancestor is the lowest node containing BOTH
 * endpoints — if the selection is entirely inside `<b>`, that's still inside
 * `<b>` (or `<b>` itself); if the selection only partially overlaps a `<b>`
 * (extends outside it on either side), the common ancestor sits at or above
 * `<b>`'s parent, so the walk never reaches `<b>` and the format reads
 * inactive. That "partially-bold reads inactive" case is a documented v1
 * limitation (Key Technical Decision 6), not a bug.
 *
 * `b`/`i`/`u` are looked up via the alias-aware `closestFormatAncestor` (not
 * `closestAncestorTag`) so that Gong-authored `<strong>`/`<em>` read as
 * Bold/Italic too (Codex adversarial review follow-up) — `ul`/`ol` have no
 * alias concept and keep the exact-match lookup.
 */
function getActiveFormats(range: Range, root: HTMLElement): Set<ActiveFormat> {
  const active = new Set<ActiveFormat>();
  for (const tag of FORMAT_TAGS) {
    const found = tag === 'b' || tag === 'i' || tag === 'u'
      ? closestFormatAncestor(range, tag, root)
      : closestAncestorTag(range, tag, root);
    if (found) active.add(tag);
  }
  return active;
}

/**
 * Pure DOM core of the unwrap operation (L2, split out of `unwrapInlineElement`
 * for L3): move `element`'s children into its parent, in place, and remove
 * `element` itself. No caret side effect — L3 needs to call this on `<a>`
 * descendants found INSIDE a freshly-extracted, still-detached fragment,
 * where `collapseCaretAfter` would be wrong (it would thrash the live
 * selection while operating on nodes that aren't even in the document yet,
 * and could run multiple times per call). Per Key Technical Decision 7 (U2),
 * this unwraps the ENTIRE matching ancestor — if it extends beyond the
 * caller's selection, more text than selected gets un-marked. Documented v1
 * behavior; boundary-splitting is deferred.
 */
function unwrapElement(element: HTMLElement): void {
  const parent = element.parentNode;
  if (!parent) return;
  const children = Array.from(element.childNodes);
  for (const child of children) parent.insertBefore(child, element);
  parent.removeChild(element);
}

/**
 * Toggle-off for an inline mark (bug 2, U2 point 4): `unwrapElement` plus
 * caret restoration — caret is left just after the last unwrapped node
 * (mirrors `collapseCaretAfter`'s existing "continue typing past this point"
 * intent). Used by callers operating on a live, in-document element (inline
 * format toggle-off, link unlink); L3's detached-fragment unwrap uses the
 * caret-free `unwrapElement` directly instead.
 */
function unwrapInlineElement(element: HTMLElement): void {
  const children = Array.from(element.childNodes);
  unwrapElement(element);
  const lastChild = children[children.length - 1];
  if (lastChild) collapseCaretAfter(lastChild);
}

function ToolbarButton({
  label,
  icon,
  disabled,
  active,
  onActivate,
}: {
  label: string;
  icon: IconType;
  disabled: boolean;
  /** All six toolbar buttons are toggles as of L2 (Link tracks `aria-pressed` too — see `FORMAT_TAGS`), so every call site passes this explicitly. */
  active: boolean;
  onActivate: () => void;
}) {
  return (
    <IconButton
      icon={icon}
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      variant="ghost"
      size="xs"
      // See the file doc comment — preventDefault here is what keeps the
      // editor's selection alive into the click handler below.
      onMouseDown={e => { e.preventDefault(); }}
      onClick={onActivate}
      className={active ? 'bg-bg-blue-light text-content-action' : ''}
    />
  );
}

export function RichTextBodyField({
  value,
  seedKey,
  onChange,
  className,
  disabled = false,
  'aria-label': ariaLabel,
}: RichTextBodyFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Which formats the caret/selection is currently inside — drives each
   * `ToolbarButton`'s `active`/`aria-pressed` (bug 2, U2). Recomputed on two
   * triggers: (1) the document-level `selectionchange` listener below, so
   * moving the caret around updates the toolbar even without clicking a
   * button; (2) explicitly after every command runs, so a click's own effect
   * on the DOM/selection is reflected immediately rather than waiting for a
   * `selectionchange` event that may fire on a different tick.
   */
  const [activeFormats, setActiveFormats] = useState<Set<ActiveFormat>>(new Set());

  const recomputeActiveFormats = useCallback(() => {
    const root = getEditableRoot(containerRef.current);
    if (!root) {
      setActiveFormats(new Set());
      return;
    }
    const range = getOwnRange(root);
    if (!range) {
      setActiveFormats(new Set());
      return;
    }
    setActiveFormats(getActiveFormats(range, root));
  }, []);

  // Plain add-in-effect/remove-in-cleanup — no lifetime ref, so this is
  // StrictMode-safe by construction (double-invoke in dev just adds then
  // removes then adds the same listener; no stale-flag hazard).
  useEffect(() => {
    document.addEventListener('selectionchange', recomputeActiveFormats);
    return () => document.removeEventListener('selectionchange', recomputeActiveFormats);
  }, [recomputeActiveFormats]);

  const applyInline = useCallback(
    (tagName: 'b' | 'i' | 'u') => {
      if (disabled) return;
      const root = getEditableRoot(containerRef.current);
      if (!root) return;
      const range = getOwnRange(root);
      if (!range) return;

      // Toggle-off (bug 2, U2 point 4): if the caret/selection is already
      // inside a matching `<tagName>` OR its canonical alias (Codex review
      // follow-up — `<strong>`/`<em>` count as Bold/Italic, see
      // `closestFormatAncestor`), unwrap it — works for a collapsed caret
      // too, unlike the apply path below, which keeps its existing
      // collapsed-selection no-op guard. When the match is the alias (e.g. a
      // `<strong>`, not a `<b>`), this unwraps THAT element directly —
      // `unwrapInlineElement` is generic over any `HTMLElement`.
      const activeAncestor = closestFormatAncestor(range, tagName, root);
      if (activeAncestor) {
        unwrapInlineElement(activeAncestor);
        onChange(root.innerHTML);
        recomputeActiveFormats();
        return;
      }

      if (range.collapsed) return;
      const wrapped = wrapRangeIn(range, tagName);
      collapseCaretAfter(wrapped);
      onChange(root.innerHTML);
      recomputeActiveFormats();
    },
    [disabled, onChange, recomputeActiveFormats],
  );

  const applyList = useCallback(
    (listTag: 'ul' | 'ol') => {
      if (disabled) return;
      const root = getEditableRoot(containerRef.current);
      if (!root) return;
      const range = getOwnRange(root);
      if (!range) return;

      // List toggle-off (bug 2/3, U2 point 5 + U4's `unwrapList`): if the
      // selection is already inside a same-type list, undo it instead of
      // nesting a second list. Opposite-type list, or no list: fall through
      // to the existing wrap behavior.
      const activeList = closestAncestorTag(range, listTag, root);
      if (activeList) {
        // bug 4b (Codex review follow-up): restore the caret near whichever
        // <li> it was actually in, not always the first one — find that
        // <li>'s index among the list's children before unwrapping mutates
        // the DOM out from under us.
        const caretLi = closestAncestorTag(range, 'li', root);
        const liChildren = Array.from(activeList.children);
        const caretLineIndex = caretLi ? liChildren.indexOf(caretLi) : 0;
        unwrapList(activeList, root, caretLineIndex < 0 ? 0 : caretLineIndex);
        onChange(root.innerHTML);
        recomputeActiveFormats();
        return;
      }

      if (range.collapsed) return;
      const inserted = wrapRangeAsList(range, listTag);
      // bug 4c (Codex review follow-up): a blank/whitespace-only selection
      // resolves to zero lines — `wrapRangeAsList` returns `null` and has
      // left the DOM untouched; no-op here too, same as the collapsed-range
      // guard above.
      if (!inserted) return;
      collapseCaretAfter(inserted);
      onChange(root.innerHTML);
      recomputeActiveFormats();
    },
    [disabled, onChange, recomputeActiveFormats],
  );

  const applyLink = useCallback(() => {
    // Link is now a toggle (L2): the no-prompt rule applies to (a) a
    // collapsed caret/selection OUTSIDE any link — there is no sane text to
    // turn into a link, so this must no-op WITHOUT ever prompting (unchanged
    // from before L2, R6) — and (b) the unlink branch immediately below,
    // which needs no URL at all. Only the insert path (an expanded selection
    // outside any link) ever prompts.
    if (disabled) return;
    const root = getEditableRoot(containerRef.current);
    if (!root) return;
    const range = getOwnRange(root);
    if (!range) return;

    // Unlink (L2, bug 2/3): if the caret/selection is already inside an
    // `<a>`, remove it — text preserved, no prompt. Works from a collapsed
    // caret too (unlike the insert path below), mirroring `applyInline`'s
    // toggle-off branch. Per Key Technical Decision 7/U2's whole-ancestor
    // stance (see plan Scope Boundaries), this always unwraps the ENTIRE
    // matching `<a>`, even for a selection covering only part of its text.
    const anchor = closestAncestorTag(range, 'a', root);
    if (anchor) {
      unwrapInlineElement(anchor);
      onChange(root.innerHTML);
      recomputeActiveFormats();
      return;
    }

    if (range.collapsed) return;
    // Clone now: a `Range` stays valid independent of `window.getSelection()`,
    // and `window.prompt` below is a blocking native dialog that can clear or
    // relocate the live selection on return in some browsers — operating on
    // the clone means the link lands on the text the rep actually selected
    // regardless of what the browser does to the Selection object meanwhile.
    const savedRange = range.cloneRange();
    const url = window.prompt('Link URL');
    if (!url) return;
    const wrapper = wrapRangeIn(savedRange, 'a', { href: url });
    // Nested-<a> prevention (L3, bug 3): a selection spanning a partial
    // existing link plus other text reaches this insert path (the
    // fully-inside-one-link shape is intercepted above by the unlink branch,
    // and the fully-inside-one-text-node shape never clones an ancestor per
    // `Range.extractContents`'s spec). `wrapRangeIn` calls `extractContents`
    // internally, and when a selection crosses OUT of an inline element like
    // `<a>`, `extractContents` clones that ancestor around the extracted
    // portion (documented DOM behavior — it does NOT clone when the whole
    // selection sits inside one text node). Wrapping that fragment in a NEW
    // `<a>` therefore produces a nested `<a>` — invalid markup that a later
    // innerHTML reseed (`RichHtmlField`) un-nests DESTRUCTIVELY. Fix: unwrap
    // any `<a>` descendants of the new wrapper immediately — "new link wins"
    // (Key Technical Decision 4): the selected text carries the NEW link, and
    // the unselected remainder of the old link (left outside `wrapper` in the
    // live document) keeps its OLD href untouched. `Array.from` snapshots the
    // NodeList before `unwrapElement` mutates the DOM out from under it; the
    // caret-free `unwrapElement` (not `unwrapInlineElement`) is required here
    // because there can be more than one nested `<a>` to unwrap — each call
    // to `unwrapInlineElement` would move the live caret via
    // `collapseCaretAfter`, thrashing the selection mid-loop; the single,
    // correct caret placement is the explicit `collapseCaretAfter(wrapper)`
    // call below, after all nesting is resolved.
    for (const nested of Array.from(wrapper.querySelectorAll('a'))) unwrapElement(nested);
    collapseCaretAfter(wrapper);
    onChange(root.innerHTML);
    recomputeActiveFormats();
  }, [disabled, onChange, recomputeActiveFormats]);

  return (
    <div ref={containerRef}>
      <div role="toolbar" aria-label="Formatting" className="flex items-center gap-0.5 mb-1 rounded border border-border-secondary bg-bg-secondary p-0.5 w-fit">
        <ToolbarButton label="Bold" icon={TextB} disabled={disabled} active={activeFormats.has('b')} onActivate={() => applyInline('b')} />
        <ToolbarButton label="Italic" icon={TextItalic} disabled={disabled} active={activeFormats.has('i')} onActivate={() => applyInline('i')} />
        <ToolbarButton label="Underline" icon={TextUnderline} disabled={disabled} active={activeFormats.has('u')} onActivate={() => applyInline('u')} />
        <ToolbarButton label="Insert link" icon={LinkSimple} disabled={disabled} active={activeFormats.has('a')} onActivate={applyLink} />
        <ToolbarButton label="Bulleted list" icon={ListBullets} disabled={disabled} active={activeFormats.has('ul')} onActivate={() => applyList('ul')} />
        <ToolbarButton label="Numbered list" icon={ListNumbers} disabled={disabled} active={activeFormats.has('ol')} onActivate={() => applyList('ol')} />
      </div>
      <RichHtmlField
        value={value}
        seedKey={seedKey}
        onChange={onChange}
        className={cn(className, '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-content-action [&_a]:underline')}
        disabled={disabled}
        aria-label={ariaLabel}
      />
    </div>
  );
}
